import React, { useState, useEffect, useRef } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Plus, BarChart3, Users, ShoppingCart, Settings, MessageCircle, LogOut, User, Activity, Loader2, LogOut as LogOutIcon } from 'lucide-react';
import ProductGrid from './ProductGrid';
import DashboardStats from './DashboardStats';
import AddProductModal from './AddProductModal';
import AdminPanel from './AdminPanel';
import MessagingPanel from './MessagingPanel';
import ActivityDashboard from './ActivityDashboard';

import ProfilePicture from './ProfilePicture';
import ExpandedProductView from './ExpandedProductView';
import NotificationBell from './NotificationBell';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import api from '../utils/api';


const API = "/api";

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('product_development');
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [tabLoading, setTabLoading] = useState(false);
  const [currentTabData, setCurrentTabData] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState(null);
  const [highlightNoteId, setHighlightNoteId] = useState(null);
  const [currentProductSession, setCurrentProductSession] = useState(null);
  const { toast } = useToast();
  const { user, logout, hasPermission } = useAuth();
  const abortControllerRef = useRef(null);

  // Debug: Log when Dashboard component mounts and user info
  console.log('🚀 Dashboard component mounted');
  console.log('👤 Current user:', user);
  const expandedProductRef = useRef(null);

  const departments = [
    {
      id: 'activity',
      name: 'Activity Dashboard',
      icon: Activity,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-500',
      hoverColor: 'hover:bg-green-100',
      textColor: 'text-green-700',
      borderColor: 'border-green-200'
    },
    {
      id: 'product_development',
      name: 'Product Development',
      icon: BarChart3,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-100',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200'
    },
    {
      id: 'product_marketing',
      name: 'Product Marketing',
      icon: Users,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-100',
      textColor: 'text-purple-700',
      borderColor: 'border-purple-200'
    },
    {
      id: 'sales',
      name: 'Sales',
      icon: ShoppingCart,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-500',
      hoverColor: 'hover:bg-green-100',
      textColor: 'text-green-700',
      borderColor: 'border-green-200'
    },
    {
      id: 'messages',
      name: 'Messages',
      icon: MessageCircle,
      color: 'from-teal-500 to-teal-600',
      bgColor: 'bg-teal-500',
      hoverColor: 'hover:bg-teal-100',
      textColor: 'text-teal-700',
      borderColor: 'border-teal-200'
    },
    ...(hasPermission('manage_users') ? [{
      id: 'admin',
      name: 'Admin',
      icon: Settings,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-500',
      hoverColor: 'hover:bg-orange-100',
      textColor: 'text-orange-700',
      borderColor: 'border-orange-200'
    }] : [])
  ];

  const fetchWithRetry = async (requestFn, maxRetries = 3) => {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;
        
        // Don't retry on cancellation
        if (error.name === 'CanceledError') {
          throw error;
        }
        
        // Don't retry on last attempt
        if (attempt === maxRetries) {
          break;
        }
        
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }
    
    throw lastError;
  };

  const fetchProducts = async (department = activeTab, priority = null) => {
    // Don't fetch products for admin, messages, or activity tabs
    if (department === 'admin' || department === 'messages' || department === 'activity') return;
    
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setTabLoading(true);
    
    try {
      let url = `/products?department=${department}`;
      if (priority && priority !== 'all') {
        url += `&priority=${priority}`;
      }
      
      const response = await fetchWithRetry(async () => {
        return await api.get(url, {
          signal: abortControllerRef.current.signal
        });
      });
      
      setProducts(response.data);
      setCurrentTabData(department);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        toast({
          title: "Error",
          description: "Failed to fetch products. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setTabLoading(false);
      abortControllerRef.current = null;
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetchWithRetry(async () => {
        return await api.get('/dashboard/stats');
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  // Fetch current product session
  const fetchCurrentProductSession = async () => {
    try {
      const response = await axios.get(`${API}/user/current-product-session`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
        }
      });
      setCurrentProductSession(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch current product session:', error);
      }
      setCurrentProductSession({ logged_in: false });
    }
  };

  // Handle product logout from header
  const handleProductLogout = async () => {
    if (!currentProductSession?.logged_in) return;
    
    try {
      await axios.post(
        `${API}/products/${currentProductSession.product_id}/logout`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
          }
        }
      );
      
      setCurrentProductSession({ logged_in: false });
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('productSessionChange', {
        detail: { logged_in: false }
      }));
      
      toast({
        title: "Success",
        description: `Logged out from ${currentProductSession.product_title}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to logout from product",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    console.log('🔄 Dashboard useEffect running, activeTab:', activeTab);

    try {
      // Only fetch products if it's a product tab
      if (!['admin', 'messages', 'activity'].includes(activeTab)) {
        console.log('📦 Fetching products for tab:', activeTab);
        // Always fetch when activeTab changes OR priorityFilter changes
        fetchProducts(activeTab, priorityFilter);
      } else {
        // For non-product tabs, just clear the selected product
        console.log('🗑️  Clearing selected product for non-product tab');
        setSelectedProduct(null);
      }
      console.log('📊 Fetching stats...');
      fetchStats();
      console.log('🎯 Fetching current product session...');
      fetchCurrentProductSession();
      setSelectedProduct(null); // Clear selected product when changing tabs
    } catch (error) {
      console.error('❌ Error in Dashboard useEffect:', error);
    }
    
    // Set up event listener for product session changes
    const handleSessionChange = (event) => {
      const sessionData = event.detail;
      if (sessionData.logged_in) {
        setCurrentProductSession(sessionData);
      } else {
        setCurrentProductSession({ logged_in: false });
      }
    };
    
    window.addEventListener('productSessionChange', handleSessionChange);
    
    // Set up auto-logout checker
    const autoLogoutInterval = setInterval(() => {
      if (currentProductSession?.logged_in && currentProductSession.auto_logout_time) {
        const autoLogoutTime = new Date(currentProductSession.auto_logout_time);
        const now = new Date();
        
        if (now >= autoLogoutTime) {
          // Session has expired, log out automatically
          handleProductLogout();
          toast({
            title: "Auto Logout",
            description: "You have been automatically logged out due to session expiration",
            variant: "destructive",
          });
        }
      }
    }, 60000); // Check every minute
    
    // Cleanup function to cancel pending requests and remove event listener
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      window.removeEventListener('productSessionChange', handleSessionChange);
      clearInterval(autoLogoutInterval);
    };
  }, [activeTab, priorityFilter]);

  // Auto-scroll to expanded product when selected
  useEffect(() => {
    if (selectedProduct && expandedProductRef.current) {
      setTimeout(() => {
        expandedProductRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }, 100); // Small delay to ensure the DOM has updated
    }
  }, [selectedProduct]);

  // Handle opening product for specific note (from notifications)
  useEffect(() => {
    const handleOpenProductForNote = (event) => {
      const { productId, noteId } = event.detail;
      
      // Switch to product development tab
      setActiveTab('product_development');
      
      // Set the note ID to highlight
      setHighlightNoteId(noteId);
      
      // Fetch the specific product
      const fetchProduct = async () => {
        try {
          const response = await api.get(`/products/${productId}`);
          setSelectedProduct(response.data);
        } catch (error) {
          console.error('Error fetching product for notification:', error);
          toast({
            title: "Error",
            description: "Could not open the product. Please try again.",
            variant: "destructive",
          });
        }
      };
      
      fetchProduct();
    };
    
    window.addEventListener('openProductForNote', handleOpenProductForNote);
    return () => window.removeEventListener('openProductForNote', handleOpenProductForNote);
  }, []);

  const handleAddProduct = async (productData) => {
    try {
      await api.post('/products', {
        ...productData,
        department: activeTab
      });
      await fetchProducts(activeTab, priorityFilter);
      await fetchStats();
      setShowAddModal(false);
      toast({
        title: "Success",
        description: "Product created successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create product",
        variant: "destructive",
      });
    }
  };

  const handleProductSelect = async (productOrId) => {
    // Handle both product object and product ID string
    if (typeof productOrId === 'string') {
      // It's a product ID, fetch the full product
      try {
        console.log('Dashboard: Fetching product by ID:', productOrId);
        const response = await api.get(`/products/${productOrId}`);
        const product = response.data;
        console.log('Dashboard: Product selected for expansion:', product.title);
        setSelectedProduct(product);
      } catch (error) {
        console.error('Error fetching product by ID:', error);
        toast({
          title: "Error",
          description: "Could not load the product. Please try again.",
          variant: "destructive",
        });
      }
    } else {
      // It's already a product object
      console.log('Dashboard: Product selected for expansion:', productOrId.title);
      setSelectedProduct(productOrId);
    }
  };

  const handleCloseExpanded = () => {
    setSelectedProduct(null);
    setHighlightNoteId(null);
  };

  const handleCardClick = async (priority) => {
    // If clicking the same priority that's already selected, deselect it
    if (priorityFilter === priority) {
      setPriorityFilter(null);
      await fetchProducts('product_development', null);
      toast({
        title: "Filter Cleared",
        description: "Showing all products",
      });
      return;
    }
    
    // Set the active tab to product_development to show products
    setActiveTab('product_development');
    setPriorityFilter(priority);
    
    // Fetch products with the priority filter
    await fetchProducts('product_development', priority);
    
    // Show toast notification
    const priorityText = priority === 'all' ? 'all products' : `${priority} priority products`;
    toast({
      title: "Filter Applied",
      description: `Showing ${priorityText}`,
    });
  };

  const handleProductUpdate = () => {
    fetchProducts(activeTab, priorityFilter);
    fetchStats();
    // Refresh the selected product if it's currently expanded
    if (selectedProduct) {
      fetchProducts(activeTab, priorityFilter).then(() => {
        const updatedProduct = products.find(p => p.id === selectedProduct.id);
        if (updatedProduct) {
          setSelectedProduct(updatedProduct);
        }
      });
    }
  };

  const activeDepartment = departments.find(d => d.id === activeTab);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                ExledOS
              </h1>
              <p className="text-slate-600 mt-1">
                Streamline your product development workflow
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {/* Add Product Button */}
              {hasPermission('create_product') && ['product_development', 'product_marketing', 'sales'].includes(activeTab) && (
                <Button
                  onClick={() => setShowAddModal(true)}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 hover:transform hover:scale-105"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Product
                </Button>
              )}

              {/* User Profile Picture */}
              <div className="flex items-center space-x-3">
                {/* Notification Bell */}
                <NotificationBell />
                
                <div className="text-right hidden sm:block">
                  <div className="text-sm font-medium">{user?.full_name}</div>
                  <div className="text-sm text-slate-500 capitalize">
                    {user?.role}
                    {currentProductSession?.logged_in && (
                      <>
                        <span className="mx-1">•</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleProductSelect(currentProductSession.product_id)}
                          className="text-green-600 hover:text-green-700 font-semibold p-1 h-auto text-sm"
                          title="Open product details"
                        >
                          Logged to "{currentProductSession.product_title}"
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleProductLogout}
                          className="ml-1 h-6 w-6 p-0 text-red-600 hover:text-red-700"
                          title="Logout from product"
                        >
                          <LogOutIcon className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="ml-2">
                  <ProfilePicture size="medium" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Department Tabs - Always visible */}
        <div className="mb-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className={`grid w-full bg-slate-50 rounded-xl p-2 shadow-sm border border-slate-200`} style={{gridTemplateColumns: `repeat(${departments.length}, minmax(0, 1fr))`}}>
              {departments.map((dept) => {
                const Icon = dept.icon;
                const isActive = activeTab === dept.id;
                const isLoading = tabLoading && currentTabData === dept.id;
                return (
                  <TabsTrigger
                    key={dept.id}
                    value={dept.id}
                    disabled={isLoading}
                    className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-300 text-xs sm:text-sm ${
                      isActive 
                        ? `bg-gradient-to-r ${dept.color} text-white shadow-lg transform scale-105 border-2 border-white` 
                        : `bg-white ${dept.textColor} hover:shadow-md hover:transform hover:scale-102 border border-slate-200 ${dept.hoverColor}`
                    } ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline">{dept.name}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {departments.filter(dept => !['admin', 'messages', 'activity'].includes(dept.id)).map((dept) => (
              <TabsContent key={dept.id} value={dept.id} className="mt-6">
                {/* Stats Overview for this tab */}
                <div 
                  className="relative py-4 cursor-pointer mb-6" 
                  onClick={(e) => {
                    // Only deselect if clicking on empty space, not on a card or its children
                    if (priorityFilter) {
                      const target = e.target;
                      const isCard = target.closest('[class*="card-hover"]') || 
                                    target.closest('[class*="bg-gradient-to-br"]');
                      
                      if (!isCard && 
                          (target === e.currentTarget || 
                           target.classList.contains('grid') || 
                           target.classList.contains('animate-fade-in') ||
                           target.classList.contains('py-4'))) {
                        setPriorityFilter(null);
                        fetchProducts(dept.id, null);
                        toast({
                          title: "Filter Cleared", 
                          description: "Showing all products",
                        });
                      }
                    }
                  }}
                >
                  <DashboardStats 
                    stats={stats} 
                    onCardClick={handleCardClick} 
                    selectedPriority={priorityFilter}
                    activeTab={dept.id}
                    products={products}
                  />
                </div>

                {/* Filter Indicator */}
                {priorityFilter && (
                  <div className="mb-6 flex items-center justify-center">
                    <div className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 shadow-sm border border-slate-200">
                      <span className="text-sm text-slate-600">Active filter:</span>
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        {priorityFilter === 'all' ? 'All Products' : `${priorityFilter.charAt(0).toUpperCase() + priorityFilter.slice(1)} Priority`}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setPriorityFilter(null);
                          fetchProducts(dept.id, null);
                        }}
                        className="ml-2 relative overflow-hidden bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white border-slate-500 shadow-md hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105 before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-[3000ms] before:ease-out"
                      >
                        <span className="relative z-10 flex items-center space-x-2">
                          <span>Clear Filter</span>
                        </span>
                      </Button>
                    </div>
                  </div>
                )}

                {/* Global Expanded Product View - shows above tab content */}
                {selectedProduct && (
                  <div ref={expandedProductRef} className="mb-6 scroll-mt-4">
                    <ExpandedProductView
                      product={selectedProduct}
                      onUpdate={handleProductUpdate}
                      onClose={handleCloseExpanded}
                      highlightNoteId={highlightNoteId}
                    />
                  </div>
                )}
                
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className={`p-3 rounded-lg bg-gradient-to-r ${dept.color} text-white`}>
                        <dept.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-slate-900">
                          {dept.name}
                        </h2>
                        <p className="text-slate-600">
                          {dept.id === 'product_development' && 'Track development progress and manage product lifecycle'}
                          {dept.id === 'product_marketing' && 'Manage marketing campaigns and product positioning'}
                          {dept.id === 'sales' && 'Track sales pipeline and customer relationships'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-slate-900">
                        {products.length}
                      </div>
                      <div className="text-sm text-slate-600">
                        Active Products
                      </div>
                    </div>
                  </div>

                  <ProductGrid
                    products={products}
                    loading={tabLoading}
                    onRefresh={() => fetchProducts(dept.id, priorityFilter)}
                    department={dept.id}
                    onProductSelect={handleProductSelect}
                    selectedProductId={selectedProduct?.id}
                    priorityFilter={priorityFilter}
                  />
                </div>
              </TabsContent>
            ))}

            {/* Activity Tab */}
            <TabsContent value="activity" className="mt-6">
              {/* Stats Overview for Activity tab */}
              <div 
                className="relative py-4 cursor-pointer mb-6" 
                onClick={(e) => {
                  // Only deselect if clicking on empty space, not on a card or its children
                  if (priorityFilter) {
                    const target = e.target;
                    const isCard = target.closest('[class*="card-hover"]') || 
                                  target.closest('[class*="bg-gradient-to-br"]');
                    
                    if (!isCard && 
                        (target === e.currentTarget || 
                         target.classList.contains('grid') || 
                         target.classList.contains('animate-fade-in') ||
                         target.classList.contains('py-4'))) {
                      setPriorityFilter(null);
                      fetchProducts('activity', null);
                      toast({
                        title: "Filter Cleared", 
                        description: "Showing all activities",
                      });
                    }
                  }
                }}
              >
                <DashboardStats 
                  stats={stats} 
                  onCardClick={handleCardClick} 
                  selectedPriority={priorityFilter}
                  activeTab="activity"
                  products={products}
                />
              </div>

              {/* Global Expanded Product View - shows above tab content */}
              {selectedProduct && (
                <div ref={expandedProductRef} className="mb-6 scroll-mt-4">
                  <ExpandedProductView
                    product={selectedProduct}
                    onUpdate={handleProductUpdate}
                    onClose={handleCloseExpanded}
                    highlightNoteId={highlightNoteId}
                  />
                </div>
              )}
              
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-3 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                    <Activity className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">
                      Activity Dashboard
                    </h2>
                    <p className="text-slate-600">
                      Monitor system activity and user interactions
                    </p>
                  </div>
                </div>

                <ActivityDashboard onProductSelect={handleProductSelect} />
              </div>
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="messages" className="mt-6">
              {/* Stats Overview for Messages tab */}
              <div 
                className="relative py-4 cursor-pointer mb-6" 
                onClick={(e) => {
                  // Only deselect if clicking on empty space, not on a card or its children
                  if (priorityFilter) {
                    const target = e.target;
                    const isCard = target.closest('[class*="card-hover"]') || 
                                  target.closest('[class*="bg-gradient-to-br"]');
                    
                    if (!isCard && 
                        (target === e.currentTarget || 
                         target.classList.contains('grid') || 
                         target.classList.contains('animate-fade-in') ||
                         target.classList.contains('py-4'))) {
                      setPriorityFilter(null);
                      fetchProducts('messages', null);
                      toast({
                        title: "Filter Cleared", 
                        description: "Showing all messages",
                      });
                    }
                  }
                }}
              >
                <DashboardStats 
                  stats={stats} 
                  onCardClick={handleCardClick} 
                  selectedPriority={priorityFilter}
                  activeTab="messages"
                  products={products}
                />
              </div>

              {/* Global Expanded Product View - shows above tab content */}
              {selectedProduct && (
                <div ref={expandedProductRef} className="mb-6 scroll-mt-4">
                  <ExpandedProductView
                    product={selectedProduct}
                    onUpdate={handleProductUpdate}
                    onClose={handleCloseExpanded}
                    highlightNoteId={highlightNoteId}
                  />
                </div>
              )}
              
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-3 rounded-lg bg-gradient-to-r from-teal-500 to-cyan-600 text-white">
                    <MessageCircle className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">
                      Messages
                    </h2>
                    <p className="text-slate-600">
                      Send and receive messages with team members
                    </p>
                  </div>
                </div>

                <MessagingPanel />
              </div>
            </TabsContent>

            {/* Admin Tab */}
            <TabsContent value="admin" className="mt-6">
              {/* Global Expanded Product View - shows above tab content */}
              {selectedProduct && (
                <div ref={expandedProductRef} className="mb-6 scroll-mt-4">
                  <ExpandedProductView
                    product={selectedProduct}
                    onUpdate={handleProductUpdate}
                    onClose={handleCloseExpanded}
                    highlightNoteId={highlightNoteId}
                  />
                </div>
              )}
              
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-3 rounded-lg bg-gradient-to-r from-orange-500 to-red-600 text-white">
                    <Settings className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">
                      Admin Panel
                    </h2>
                    <p className="text-slate-600">
                      Manage users, permissions, and system configuration
                    </p>
                  </div>
                </div>

                <AdminPanel />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Add Product Modal */}
      <AddProductModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddProduct}
        department={activeTab}
      />

      
    </div>
  );
};

export default Dashboard;